/**
 * Allocation of the 8 best third-placed teams to their round-of-32 slots.
 * Ported EXACTLY from the legacy `assignThirds` (this table/algorithm is the
 * most precious extracted asset, specs/00): each slot accepts third-placed
 * teams from 5 specific groups; a backtracking pass finds an assignment, with
 * the legacy's hard-coded answer for the modal combination kept for fidelity.
 */

export interface ThirdsTable {
  /** slot id (e.g. "M74") -> group letters allowed in that slot. */
  readonly allowedGroups: Readonly<Record<string, readonly string[]>>;
  /** Slots in legacy assignment order. */
  readonly slotOrder: readonly string[];
}

export interface ThirdsAssignment {
  /** slot id -> group letter whose third-placed team plays there. */
  readonly bySlot: Readonly<Record<string, string>>;
  /** False when backtracking failed and the legacy sequential fallback ran. */
  readonly exact: boolean;
}

/** Legacy hard-coded assignment for the modal qualified set "ACDEFGIJ". */
export const LEGACY_MODAL_SET = "ACDEFGIJ";
export const LEGACY_MODAL_ASSIGNMENT: Readonly<Record<string, string>> = {
  M74: "A",
  M77: "C",
  M79: "F",
  M80: "E",
  M81: "I",
  M82: "J",
  M85: "G",
  M87: "D",
};

/**
 * Official FIFA round-of-32 third-place allocations, keyed by the SORTED string of the 8
 * qualifying groups. The backtracking below only guarantees a VALID assignment (one that
 * respects the allowed-groups table) — but FIFA's allocation is a UNIQUE per-combination
 * mapping, and many combinations admit SEVERAL valid assignments, so backtracking can pick
 * the wrong one (e.g. BDEFIJKL → it returns M74=B/M77=D/M81=F where FIFA says D/F/B). For
 * combinations that actually occur we therefore PIN the official answer, verified against
 * FIFA's published bracket.
 */
export const KNOWN_THIRDS_ASSIGNMENTS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  [LEGACY_MODAL_SET]: LEGACY_MODAL_ASSIGNMENT,
  // WC 2026 — actual qualified set (thirds from groups B,D,E,F,I,J,K,L). Verified vs the
  // official round of 32: 1E–3D (Germany–Paraguay), 1I–3F (France–Sweden), 1D–3B (USA–
  // Bosnia), 1A–3E (Mexico–Ecuador), 1L–3K (England–DR Congo), 1G–3I (Belgium–Senegal),
  // 1B–3J (Switzerland–Algeria), 1K–3L (Colombia–Ghana).
  BDEFIJKL: { M74: "D", M77: "F", M79: "E", M80: "K", M81: "B", M82: "I", M85: "J", M87: "L" },
};

/**
 * Assign the 8 qualified groups (letters) to the 8 third-place slots.
 * Official pinned answer first (KNOWN_THIRDS_ASSIGNMENTS), otherwise deterministic
 * backtracking: slots filled in `slotOrder`, candidate groups tried alphabetically,
 * first complete solution wins (legacy behaviour — approximate for unpinned combos).
 */
export function assignThirds(qualifiedGroups: readonly string[], table: ThirdsTable): ThirdsAssignment {
  const set = [...qualifiedGroups].sort();
  const known = KNOWN_THIRDS_ASSIGNMENTS[set.join("")];
  if (known) {
    return { bySlot: { ...known }, exact: true };
  }
  const used = new Set<string>();
  const res: Record<string, string> = {};
  const backtrack = (k: number): boolean => {
    if (k === table.slotOrder.length) return true;
    const slot = table.slotOrder[k];
    if (slot === undefined) return false;
    const allowed = table.allowedGroups[slot] ?? [];
    for (const g of set) {
      if (used.has(g) || !allowed.includes(g)) continue;
      used.add(g);
      res[slot] = g;
      if (backtrack(k + 1)) return true;
      used.delete(g);
      delete res[slot];
    }
    return false;
  };
  if (backtrack(0)) return { bySlot: res, exact: true };

  // Legacy fallback: sequential assignment, may violate the allowed-group
  // table (kept for strict fidelity; flagged so callers can surface it).
  const fallback: Record<string, string> = {};
  set.forEach((g, i) => {
    const slot = table.slotOrder[i];
    if (slot !== undefined) fallback[slot] = g;
  });
  return { bySlot: fallback, exact: false };
}
